import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';

import type { SeatType, User } from '@/types';

import MembersTable, {
  emptyMembersTableFacets,
} from '@/features/membersTable/MembersTable';
import {
  ALL_MEMBERS_TABLE_COLUMNS,
  ALL_MEMBERS_TABLE_COLUMNS_WITH_PAUSE,
} from '@/features/membersTable/columns';
import { mapMembersTableRow } from '@/features/membersTable/mapMembersTableRow';
import { resolveViewerSessionZone } from '@/features/membersTable/viewerSessionTime';
import {
  type MembersTableCoachCountFilter,
  type MembersTableSortBy,
  type MembersTableSortDirection,
  useGetMembersTableQuery,
} from '@/store/api/userApi';
import { hasGlobalRole, isAdmin, isCoach, isSupport } from '@/types/user';

const PAGE_SIZE = 50;
const DEFAULT_SORT_FIELD: MembersTableSortBy = 'joined_at';
const DEFAULT_SORT_DIRECTION: MembersTableSortDirection = 'desc';
const SORT_FIELDS: MembersTableSortBy[] = [
  'name',
  'company',
  'email',
  'joined_at',
  'contract_end_date',
  'end_date_status',
  'coach_name',
  'member_status',
  'seat_type',
];
const SORT_DIRECTIONS: MembersTableSortDirection[] = ['asc', 'desc'];
const COACH_COUNT_FILTERS: MembersTableCoachCountFilter[] = [
  'none',
  'one',
  'two',
  'three_plus',
];
const SESSION_FILTERS = [
  'no_upcoming_sessions',
  'scheduled_sessions',
  'chem_call',
] as const;

// HubSpot pause dates are an Ops view; the backend sends them only to global admin/support.
const membersTableColumns = (user: User) =>
  hasGlobalRole(user, 'admin') || hasGlobalRole(user, 'support')
    ? ALL_MEMBERS_TABLE_COLUMNS_WITH_PAUSE
    : ALL_MEMBERS_TABLE_COLUMNS;

const arrayParam = (params: URLSearchParams, key: string) =>
  params
    .getAll(key)
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter(Boolean);

const typedArrayParam = <T extends string>(
  params: URLSearchParams,
  key: string,
  allowedValues: readonly T[],
): T[] =>
  arrayParam(params, key).filter((value): value is T =>
    allowedValues.includes(value as T),
  );

const pageParam = (params: URLSearchParams) => {
  const value = Number(params.get('page'));
  return Number.isFinite(value) && value > 0 ? value - 1 : 0;
};

const sortFieldParam = (params: URLSearchParams): MembersTableSortBy => {
  const value = params.get('sort_by');
  return SORT_FIELDS.includes(value as MembersTableSortBy)
    ? (value as MembersTableSortBy)
    : DEFAULT_SORT_FIELD;
};

const sortDirectionParam = (
  params: URLSearchParams,
): MembersTableSortDirection => {
  const value = params.get('sort_direction');
  return SORT_DIRECTIONS.includes(value as MembersTableSortDirection)
    ? (value as MembersTableSortDirection)
    : DEFAULT_SORT_DIRECTION;
};

const setArrayParam = (
  params: URLSearchParams,
  key: string,
  values?: string[],
) => {
  params.delete(key);
  values?.filter(Boolean).forEach((value) => params.append(key, value));
};

const Members = () => {
  const { isUserLoading, user = {} as User } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = pageParam(searchParams);
  const debouncedSearchQuery = searchParams.get('search') ?? '';
  const businessPlanFilter = arrayParam(searchParams, 'business_plans');
  const companyFilter = arrayParam(searchParams, 'company_ids');
  const coachFilter = arrayParam(searchParams, 'coach_ids');
  const endDateStatusFilter = arrayParam(searchParams, 'end_date_statuses');
  const coachesAssignedFilter = typedArrayParam(
    searchParams,
    'coaches_assigned',
    COACH_COUNT_FILTERS,
  );
  const growthPlanStartedFilter = arrayParam(
    searchParams,
    'growth_plan_started',
  );
  const insightsStatusFilter = arrayParam(searchParams, 'insights_statuses');
  const memberStatusFilter = arrayParam(searchParams, 'member_statuses');
  const seatTypeFilter = arrayParam(searchParams, 'seat_types');
  const noUpcomingSessions =
    searchParams.get('no_upcoming_sessions') === 'true';
  const scheduledSessions = searchParams.get('scheduled_sessions') === 'true';
  const chemCall = searchParams.get('chem_call') === 'true';
  const sessionFilter = SESSION_FILTERS.filter(
    (filter) => searchParams.get(filter) === 'true',
  );
  const sortField = sortFieldParam(searchParams);
  const sortDirection = sortDirectionParam(searchParams);
  const [searchQuery, setSearchQuery] = useState(debouncedSearchQuery);
  const showCoachFilter = !(
    isCoach(user) &&
    !isAdmin(user) &&
    !isSupport(user)
  );

  const updateSearchParams = useCallback(
    (
      updates: Record<string, string | string[] | boolean | undefined>,
      { resetPage = true } = {},
    ) => {
      setSearchParams((currentParams) => {
        const nextParams = new URLSearchParams(currentParams);

        Object.entries(updates).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            setArrayParam(nextParams, key, value);
          } else if (typeof value === 'boolean') {
            if (value) {
              nextParams.set(key, 'true');
            } else {
              nextParams.delete(key);
            }
          } else if (value) {
            nextParams.set(key, value);
          } else {
            nextParams.delete(key);
          }
        });

        if (resetPage) {
          nextParams.delete('page');
        }

        return nextParams;
      });
    },
    [setSearchParams],
  );

  const setPage = (nextPage: number) => {
    updateSearchParams(
      { page: nextPage > 0 ? String(nextPage + 1) : undefined },
      { resetPage: false },
    );
  };

  useEffect(() => {
    setSearchQuery(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (searchQuery === debouncedSearchQuery) return undefined;

    const timeoutId = window.setTimeout(() => {
      updateSearchParams({ search: searchQuery });
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [debouncedSearchQuery, searchQuery, updateSearchParams]);

  const membersTableQuery = useGetMembersTableQuery(
    {
      businessPlans: businessPlanFilter,
      chemCall: chemCall || undefined,
      coachIds: showCoachFilter ? coachFilter : [],
      coachesAssigned: coachesAssignedFilter,
      companyIds: companyFilter,
      endDateStatuses: endDateStatusFilter,
      growthPlanStarted: growthPlanStartedFilter,
      insightsStatuses: insightsStatusFilter,
      limit: PAGE_SIZE,
      memberStatuses: memberStatusFilter,
      noUpcomingSessions: noUpcomingSessions || undefined,
      page: page + 1,
      scheduledSessions: scheduledSessions || undefined,
      search: debouncedSearchQuery || undefined,
      seatTypes: seatTypeFilter as SeatType[],
      sortBy: sortField,
      sortDirection,
    },
    { skip: isUserLoading },
  );

  const viewerZone = resolveViewerSessionZone();
  const zoneKey = viewerZone.status === 'ready' ? viewerZone.timeZone : null;
  const rows = useMemo(
    () =>
      membersTableQuery.data?.items.map((item) =>
        mapMembersTableRow(item, viewerZone),
      ) ?? [],
    // The primitive zone key changes when the browser's runtime zone changes.
    [membersTableQuery.data?.items, zoneKey],
  );

  const handleSortChange = (field: MembersTableSortBy) => {
    if (sortField === field) {
      updateSearchParams({
        sort_direction: sortDirection === 'asc' ? 'desc' : 'asc',
      });
    } else {
      updateSearchParams({ sort_by: field, sort_direction: 'asc' });
    }
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    updateSearchParams({
      business_plans: [],
      chem_call: false,
      coach_ids: [],
      coaches_assigned: [],
      company_ids: [],
      end_date_statuses: [],
      growth_plan_started: [],
      insights_statuses: [],
      member_statuses: [],
      no_upcoming_sessions: false,
      scheduled_sessions: false,
      search: undefined,
      seat_types: [],
    });
  };

  if (isUserLoading) {
    return null;
  }

  return (
    <div className="bg-sage-100 px-6 pb-6 pt-12">
      <MembersTable
        businessPlanFilter={businessPlanFilter}
        coachesAssignedFilter={coachesAssignedFilter}
        coachFilter={showCoachFilter ? coachFilter : []}
        columns={membersTableColumns(user)}
        companyFilter={companyFilter}
        endDateStatusFilter={endDateStatusFilter}
        error={membersTableQuery.isError}
        facets={{
          ...emptyMembersTableFacets,
          ...membersTableQuery.data?.facets,
        }}
        growthPlanStartedFilter={growthPlanStartedFilter}
        insightsStatusFilter={insightsStatusFilter}
        isFetching={membersTableQuery.isFetching}
        loading={membersTableQuery.isLoading}
        memberStatusFilter={memberStatusFilter}
        page={page}
        rows={rows}
        searchQuery={searchQuery}
        seatTypeFilter={seatTypeFilter}
        sessionFilter={sessionFilter}
        showCoachFilter={showCoachFilter}
        sortDirection={sortDirection}
        sortField={sortField}
        titleVariant="h4"
        total={membersTableQuery.data?.total ?? 0}
        totalPages={membersTableQuery.data?.pages ?? 0}
        onBusinessPlanFilterChange={(selected) => {
          updateSearchParams({ business_plans: selected });
        }}
        onClearAllFilters={clearAllFilters}
        onCoachesAssignedFilterChange={(selected) => {
          updateSearchParams({ coaches_assigned: selected });
        }}
        onCoachFilterChange={(selected) => {
          updateSearchParams({ coach_ids: selected });
        }}
        onCompanyFilterChange={(selected) => {
          updateSearchParams({ company_ids: selected });
        }}
        onEndDateStatusFilterChange={(selected) => {
          updateSearchParams({ end_date_statuses: selected });
        }}
        onGrowthPlanStartedFilterChange={(selected) => {
          updateSearchParams({ growth_plan_started: selected });
        }}
        onInsightsStatusFilterChange={(selected) => {
          updateSearchParams({ insights_statuses: selected });
        }}
        onMemberStatusFilterChange={(selected) => {
          updateSearchParams({ member_statuses: selected });
        }}
        onPageChange={setPage}
        onSearchChange={setSearchQuery}
        onSeatTypeFilterChange={(selected) => {
          updateSearchParams({ seat_types: selected });
        }}
        onSessionFilterChange={(selected) => {
          updateSearchParams({
            chem_call: selected.includes('chem_call'),
            no_upcoming_sessions: selected.includes('no_upcoming_sessions'),
            scheduled_sessions: selected.includes('scheduled_sessions'),
          });
        }}
        onSortChange={handleSortChange}
      />
    </div>
  );
};

export default Members;
